import { PlayerMatch, Rating3SubSet } from '../../models/PlayerMatch';

// Pure data helpers for the redesigned match page (/match2).
// Null semantics: rating30/swingPct/e* being null/undefined means the Rating 3.0
// engine didn't run for that map — they must surface as null (rendered "–"), never 0.

export type Side = 'all' | 't' | 'ct';
export type Match2Tab = 'overview' | 'detailed' | 'matrix' | 'clips' | 'replay' | 'swing';

// Normalized per-side view over a PlayerMatch row, so render code never touches
// tSide*/ctSide* field names directly.
export interface SideStats {
    kills?: number;
    deaths?: number;
    assists?: number;
    adr?: number;
    kast?: number;
    rating2?: number;
    rating21?: number;
    rating30: number | null;
    swingPct: number | null;
    eKills: number | null;
    eDeaths: number | null;
    eAdr: number | null;
    eKast: number | null;
    fk?: number;
    firstDeaths?: number;
    enemiesFlashed?: number;
    enemyFlashTime?: number;
    utilityDamage?: number;
    tradedDeaths: number | null;
    killsAgainst?: { [steamId: string]: number };
    roundsPlayed?: number;
}

export const getSideStats = (p: PlayerMatch, side: Side): SideStats => {
    if (side === 't') {
        return {
            kills: p.tSideKills, deaths: p.tSideDeaths, assists: p.tSideAssists, adr: p.tSideADR, kast: p.tSideKAST,
            rating2: p.tSideRating2, rating21: p.tSideRating21,
            rating30: p.tSideRating30 ?? null, swingPct: p.tSideSwingPct ?? null,
            eKills: p.tSideEKills ?? null, eDeaths: p.tSideEDeaths ?? null,
            eAdr: p.tSideEAdr ?? null, eKast: p.tSideEKast ?? null,
            fk: p.tSideFK, firstDeaths: p.tSideFirstDeaths,
            enemiesFlashed: p.tSideEnemiesFlashed, enemyFlashTime: p.tSideEnemyFlashTime,
            utilityDamage: p.tSideUtilityDamage,
            tradedDeaths: p.tSideTradedDeaths ?? null,
            killsAgainst: p.tSideKillsAgainstPlayers, roundsPlayed: p.tSideRoundsPlayed,
        };
    }
    if (side === 'ct') {
        return {
            kills: p.ctSideKills, deaths: p.ctSideDeaths, assists: p.ctSideAssists, adr: p.ctSideADR, kast: p.ctSideKAST,
            rating2: p.ctSideRating2, rating21: p.ctSideRating21,
            rating30: p.ctSideRating30 ?? null, swingPct: p.ctSideSwingPct ?? null,
            eKills: p.ctSideEKills ?? null, eDeaths: p.ctSideEDeaths ?? null,
            eAdr: p.ctSideEAdr ?? null, eKast: p.ctSideEKast ?? null,
            fk: p.ctSideFK, firstDeaths: p.ctSideFirstDeaths,
            enemiesFlashed: p.ctSideEnemiesFlashed, enemyFlashTime: p.ctSideEnemyFlashTime,
            utilityDamage: p.ctSideUtilityDamage,
            tradedDeaths: p.ctSideTradedDeaths ?? null,
            killsAgainst: p.ctSideKillsAgainstPlayers, roundsPlayed: p.ctSideRoundsPlayed,
        };
    }
    return {
        kills: p.kills, deaths: p.deaths, assists: p.assists, adr: p.adr, kast: p.kast,
        rating2: p.rating2, rating21: p.rating21,
        rating30: p.rating30 ?? null, swingPct: p.swingPct ?? null,
        eKills: p.eKills ?? null, eDeaths: p.eDeaths ?? null,
        eAdr: p.eAdr ?? null, eKast: p.eKast ?? null,
        fk: p.fk, firstDeaths: p.firstDeaths,
        enemiesFlashed: p.enemiesFlashed, enemyFlashTime: p.enemyFlashTime,
        utilityDamage: p.utilityDamage,
        tradedDeaths: p.tradedDeaths ?? null,
        killsAgainst: p.killsAgainstPlayers, roundsPlayed: p.roundsPlayed,
    };
};

export const playerName = (p: PlayerMatch): string =>
    p.account?.playerName || p.steamId.slice(-6);

// ===== Round winners =====

// Round-end reasons that mean the CT side took the round. TargetSaved is the clock
// running out, and RoundDraw carries no winner at all — both go to the CT.
export const ctWonRound = (reason: string): boolean =>
    reason === 'CTsWin' || reason === 'BombDefused' || reason === 'TargetSaved' || reason === 'RoundDraw';

// Which side team1 played in a given round, by convention: team1 starts CT, sides swap at the
// half (MR12 => round 13) and then every 3 rounds of overtime (MR3). Verified against an
// overtime upload: the half-only rule scored it 17:17, this one reproduces the stored 19:15.
// Only used for the id-less fallback below, so the MR12 assumption is safe — the CS:GO-era
// MR15 seasons all carry a real round-end team id.
export const team1IsCtInRound = (round: number): boolean =>
    round <= 24 ? round <= 12 : Math.floor((round - 25) / 3) % 2 === 0;

// Did `teamId` win this round? The round-end's team id is the truth when it's there, but
// uploaded demos don't have one: the parser resolves it by team name (DemoHandler.Events.cs)
// before the upload's synthetic team row exists, so it comes back empty. The reason is always
// present, so fall back to "which side won" against the side this team was on.
export const teamWonRound = (
    reason: string,
    teamWonID: string | null | undefined,
    teamId: string,
    otherTeamId: string | null | undefined,
    teamIsCt: boolean,
): boolean => {
    if (teamWonID) {
        if (teamWonID === teamId) return true;
        // A stored id always beats the reason fallback, which assumes team1 started CT on an
        // MR12+MR3 schedule and so misreads the MR15 seasons. Only treat a non-matching id as
        // stale when the opponent's id is known and it isn't that either — which happens when
        // an uploaded demo's teams are named, because MatchDA.RenameUploadTeamsAsync re-keys
        // the match but not the round-end JSON, leaving a dead id behind.
        if (!otherTeamId || teamWonID === otherTeamId) return false;
    }
    return ctWonRound(reason) === teamIsCt;
};

// ===== Formatting =====

export const fmtR3 = (v: number | null | undefined): string => (v == null ? '–' : v.toFixed(2));

export const fmtSwing = (v: number | null | undefined): string =>
    v == null ? '–' : `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;

// Best available legacy rating for pre-R3 matches: Rating 2.1 preferred, else 2.0.
// Null only when neither is present (e.g. a side with no rounds played).
export const legacyRating = (s: SideStats): number | null =>
    s.rating21 ?? s.rating2 ?? null;

// Whether any player on these boards has a Rating 3.0 value. When false the whole
// match predates the engine and the boards/detailed tab fall back to Rating 2.1/2.0.
export const anyHasR3 = (rows: PlayerMatch[]): boolean =>
    rows.some(r => r.rating30 != null);

// ===== Sorting: Rating 3.0 desc, nulls last, fall back to rating21/rating2 =====

const fallbackRating = (p: PlayerMatch, side: Side): number => {
    if (side === 't') return p.tSideRating21 ?? p.tSideRating2 ?? 0;
    if (side === 'ct') return p.ctSideRating21 ?? p.ctSideRating2 ?? 0;
    return p.rating21 ?? p.rating2 ?? 0;
};

export const compareRows = (side: Side) => (a: PlayerMatch, b: PlayerMatch): number => {
    const ar = getSideStats(a, side).rating30;
    const br = getSideStats(b, side).rating30;
    if (ar != null && br != null) return br - ar;
    if (ar != null) return -1;
    if (br != null) return 1;
    return fallbackRating(b, side) - fallbackRating(a, side);
};

// ===== Rating 3.0 sub-components =====
// Aggregated rows carry partially-filled sections (null components are omitted from
// the JSON), so the reader is tolerant of missing sections/keys.

export interface PartialSubs {
    both?: Partial<Rating3SubSet>;
    ct?: Partial<Rating3SubSet>;
    t?: Partial<Rating3SubSet>;
}

export const readSubs = (p: PlayerMatch): PartialSubs | null => {
    if (!p.rating3Subs) return null;
    try {
        return JSON.parse(p.rating3Subs) as PartialSubs;
    } catch {
        return null;
    }
};

export const subsForSide = (subs: PartialSubs | null, side: Side): Partial<Rating3SubSet> | undefined =>
    subs ? (side === 'all' ? subs.both : subs[side]) : undefined;

// ===== Clutches =====

export interface ClutchTier { label: string; wins: number; attempts: number; }

export const clutchTiers = (p: PlayerMatch): ClutchTier[] => [
    { label: '1v1', wins: p.clutch1v1Wins ?? 0, attempts: p.clutch1v1Attempts ?? 0 },
    { label: '1v2', wins: p.clutch1v2Wins ?? 0, attempts: p.clutch1v2Attempts ?? 0 },
    { label: '1v3', wins: p.clutch1v3Wins ?? 0, attempts: p.clutch1v3Attempts ?? 0 },
    { label: '1v4', wins: p.clutch1v4Wins ?? 0, attempts: p.clutch1v4Attempts ?? 0 },
    { label: '1v5', wins: p.clutch1v5Wins ?? 0, attempts: p.clutch1v5Attempts ?? 0 },
];

// The teammate who most often trades the selected player's death.
export const bestTraderFor = (selected: PlayerMatch, teammates: PlayerMatch[]): PlayerMatch | null => {
    let best: PlayerMatch | null = null;
    let bestCount = 0;
    for (const tm of teammates) {
        if (tm.steamId === selected.steamId) continue;
        const count = tm.tradesForPlayers?.[selected.steamId] ?? 0;
        if (count > bestCount) {
            bestCount = count;
            best = tm;
        }
    }
    return best;
};

// ===== "Alle maps" aggregation across a series' PlayerMatch rows for one player =====

type NumGetter = (r: PlayerMatch) => number | null | undefined;
type DictGetter = (r: PlayerMatch) => { [k: string]: number } | undefined;

// Sum over rows, skipping null/undefined; undefined when no row had a value.
const sumDef = (rows: PlayerMatch[], get: NumGetter): number | undefined => {
    let sum = 0;
    let any = false;
    for (const r of rows) {
        const v = get(r);
        if (v != null) { sum += v; any = true; }
    }
    return any ? sum : undefined;
};

// Same, but null when no row had a value (for the nullable Rating 3.0 fields).
const sumNull = (rows: PlayerMatch[], get: NumGetter): number | null => sumDef(rows, get) ?? null;

// Rounds-weighted average, skipping null-valued maps in both numerator and denominator.
// If rounds data is missing for the included maps (older seasons), falls back to an
// unweighted average rather than dropping the stat entirely.
const weightedAvg = (rows: PlayerMatch[], get: NumGetter, weight: NumGetter): number | null => {
    const included = rows
        .map(r => ({ v: get(r), w: weight(r) }))
        .filter((x): x is { v: number; w: number | null | undefined } => x.v != null);
    if (included.length === 0) return null;
    const useRounds = included.every(x => x.w != null && x.w > 0);
    let num = 0;
    let den = 0;
    for (const x of included) {
        const w = useRounds ? (x.w as number) : 1;
        num += x.v * w;
        den += w;
    }
    return den > 0 ? num / den : null;
};

const mergeDicts = (rows: PlayerMatch[], get: DictGetter): { [k: string]: number } | undefined => {
    let out: { [k: string]: number } | undefined;
    for (const r of rows) {
        const d = get(r);
        if (!d) continue;
        out = out || {};
        for (const [k, v] of Object.entries(d)) {
            out[k] = (out[k] || 0) + v;
        }
    }
    return out;
};

const SUB_KEYS: (keyof Rating3SubSet)[] = ['k', 'd', 'kast', 'mk', 'sw', 'adr'];

// Rounds-weighted average of the parsed rating3Subs per component, skipping maps
// without subs. Sections/components without any data are omitted (never zeroed).
const aggregateSubs = (rows: PlayerMatch[]): string | null => {
    const parsed = rows.map(r => ({ subs: readSubs(r), row: r })).filter(x => x.subs != null);
    if (parsed.length === 0) return null;

    const sections: { key: keyof PartialSubs; weight: NumGetter }[] = [
        { key: 'both', weight: r => r.roundsPlayed },
        { key: 'ct', weight: r => r.ctSideRoundsPlayed },
        { key: 't', weight: r => r.tSideRoundsPlayed },
    ];

    const result: PartialSubs = {};
    let anyValue = false;
    for (const section of sections) {
        const sectionOut: Partial<Rating3SubSet> = {};
        for (const key of SUB_KEYS) {
            const v = weightedAvg(
                parsed.map(x => x.row),
                r => {
                    const subs = readSubs(r);
                    const set = subs ? subs[section.key] : undefined;
                    return set ? set[key] : undefined;
                },
                section.weight
            );
            if (v != null) {
                sectionOut[key] = v;
                anyValue = true;
            }
        }
        if (Object.keys(sectionOut).length > 0) result[section.key] = sectionOut;
    }
    return anyValue ? JSON.stringify(result) : null;
};

// Aggregate one player's rows (one per played map) into a single series-level row.
// Counts are summed; rate stats are rounds-weighted averages (side variants weighted
// by the side rounds); null-valued maps are skipped, and all-null stays null.
export const aggregatePlayerRows = (rows: PlayerMatch[]): PlayerMatch => {
    if (rows.length === 1) return rows[0];
    const agg: PlayerMatch = { ...rows[0] };

    // --- Map-level counting stats: sums ---
    agg.kills = sumDef(rows, r => r.kills) ?? 0;
    agg.deaths = sumDef(rows, r => r.deaths) ?? 0;
    agg.assists = sumDef(rows, r => r.assists) ?? 0;
    agg.fk = sumDef(rows, r => r.fk) ?? 0;
    agg.firstDeaths = sumDef(rows, r => r.firstDeaths);
    agg.oneKillRounds = sumDef(rows, r => r.oneKillRounds);
    agg.twoKillRounds = sumDef(rows, r => r.twoKillRounds);
    agg.threeKillRounds = sumDef(rows, r => r.threeKillRounds);
    agg.fourKillRounds = sumDef(rows, r => r.fourKillRounds);
    agg.fiveKillRounds = sumDef(rows, r => r.fiveKillRounds);
    agg.enemiesFlashed = sumDef(rows, r => r.enemiesFlashed);
    agg.enemyFlashTime = sumDef(rows, r => r.enemyFlashTime);
    agg.utilityDamage = sumDef(rows, r => r.utilityDamage);
    agg.clutch1v1Attempts = sumDef(rows, r => r.clutch1v1Attempts);
    agg.clutch1v1Wins = sumDef(rows, r => r.clutch1v1Wins);
    agg.clutch1v2Attempts = sumDef(rows, r => r.clutch1v2Attempts);
    agg.clutch1v2Wins = sumDef(rows, r => r.clutch1v2Wins);
    agg.clutch1v3Attempts = sumDef(rows, r => r.clutch1v3Attempts);
    agg.clutch1v3Wins = sumDef(rows, r => r.clutch1v3Wins);
    agg.clutch1v4Attempts = sumDef(rows, r => r.clutch1v4Attempts);
    agg.clutch1v4Wins = sumDef(rows, r => r.clutch1v4Wins);
    agg.clutch1v5Attempts = sumDef(rows, r => r.clutch1v5Attempts);
    agg.clutch1v5Wins = sumDef(rows, r => r.clutch1v5Wins);
    agg.trades = sumDef(rows, r => r.trades);
    agg.tradesReceived = sumDef(rows, r => r.tradesReceived);
    agg.wallbangKills = sumDef(rows, r => r.wallbangKills);
    agg.noscopeKills = sumDef(rows, r => r.noscopeKills);
    agg.smokeKills = sumDef(rows, r => r.smokeKills);
    agg.blindKills = sumDef(rows, r => r.blindKills);
    agg.roundsPlayed = sumDef(rows, r => r.roundsPlayed);
    agg.killsAgainstPlayers = mergeDicts(rows, r => r.killsAgainstPlayers);
    agg.tradesForPlayers = mergeDicts(rows, r => r.tradesForPlayers);

    // Nullable Rating 3.0 counting stats: sums, skipping null maps; all-null stays null.
    agg.eKills = sumNull(rows, r => r.eKills);
    agg.eDeaths = sumNull(rows, r => r.eDeaths);
    agg.eOpenKills = sumNull(rows, r => r.eOpenKills);
    agg.eOpenDeaths = sumNull(rows, r => r.eOpenDeaths);
    agg.tradedDeaths = sumNull(rows, r => r.tradedDeaths);
    agg.eTraded = sumNull(rows, r => r.eTraded);

    // --- Map-level rate stats: rounds-weighted averages ---
    agg.adr = weightedAvg(rows, r => r.adr, r => r.roundsPlayed) ?? 0;
    agg.kast = weightedAvg(rows, r => r.kast, r => r.roundsPlayed) ?? 0;
    agg.rating2 = weightedAvg(rows, r => r.rating2, r => r.roundsPlayed) ?? 0;
    agg.rating21 = weightedAvg(rows, r => r.rating21, r => r.roundsPlayed) ?? undefined;
    agg.rating30 = weightedAvg(rows, r => r.rating30, r => r.roundsPlayed);
    agg.swingPct = weightedAvg(rows, r => r.swingPct, r => r.roundsPlayed);
    agg.eAdr = weightedAvg(rows, r => r.eAdr, r => r.roundsPlayed);
    agg.eKast = weightedAvg(rows, r => r.eKast, r => r.roundsPlayed);

    // --- T side ---
    agg.tSideRoundsPlayed = sumDef(rows, r => r.tSideRoundsPlayed);
    agg.tSideKills = sumDef(rows, r => r.tSideKills);
    agg.tSideDeaths = sumDef(rows, r => r.tSideDeaths);
    agg.tSideAssists = sumDef(rows, r => r.tSideAssists);
    agg.tSideFK = sumDef(rows, r => r.tSideFK);
    agg.tSideFirstDeaths = sumDef(rows, r => r.tSideFirstDeaths);
    agg.tSideEnemiesFlashed = sumDef(rows, r => r.tSideEnemiesFlashed);
    agg.tSideEnemyFlashTime = sumDef(rows, r => r.tSideEnemyFlashTime);
    agg.tSideUtilityDamage = sumDef(rows, r => r.tSideUtilityDamage);
    agg.tSideKillsAgainstPlayers = mergeDicts(rows, r => r.tSideKillsAgainstPlayers);
    agg.tSideEKills = sumNull(rows, r => r.tSideEKills);
    agg.tSideEDeaths = sumNull(rows, r => r.tSideEDeaths);
    agg.tSideEOpenKills = sumNull(rows, r => r.tSideEOpenKills);
    agg.tSideEOpenDeaths = sumNull(rows, r => r.tSideEOpenDeaths);
    agg.tSideTradedDeaths = sumNull(rows, r => r.tSideTradedDeaths);
    agg.tSideETraded = sumNull(rows, r => r.tSideETraded);
    agg.tSideADR = weightedAvg(rows, r => r.tSideADR, r => r.tSideRoundsPlayed) ?? undefined;
    agg.tSideKAST = weightedAvg(rows, r => r.tSideKAST, r => r.tSideRoundsPlayed) ?? undefined;
    agg.tSideRating2 = weightedAvg(rows, r => r.tSideRating2, r => r.tSideRoundsPlayed) ?? undefined;
    agg.tSideRating21 = weightedAvg(rows, r => r.tSideRating21, r => r.tSideRoundsPlayed) ?? undefined;
    agg.tSideRating30 = weightedAvg(rows, r => r.tSideRating30, r => r.tSideRoundsPlayed);
    agg.tSideSwingPct = weightedAvg(rows, r => r.tSideSwingPct, r => r.tSideRoundsPlayed);
    agg.tSideEAdr = weightedAvg(rows, r => r.tSideEAdr, r => r.tSideRoundsPlayed);
    agg.tSideEKast = weightedAvg(rows, r => r.tSideEKast, r => r.tSideRoundsPlayed);

    // --- CT side ---
    agg.ctSideRoundsPlayed = sumDef(rows, r => r.ctSideRoundsPlayed);
    agg.ctSideKills = sumDef(rows, r => r.ctSideKills);
    agg.ctSideDeaths = sumDef(rows, r => r.ctSideDeaths);
    agg.ctSideAssists = sumDef(rows, r => r.ctSideAssists);
    agg.ctSideFK = sumDef(rows, r => r.ctSideFK);
    agg.ctSideFirstDeaths = sumDef(rows, r => r.ctSideFirstDeaths);
    agg.ctSideEnemiesFlashed = sumDef(rows, r => r.ctSideEnemiesFlashed);
    agg.ctSideEnemyFlashTime = sumDef(rows, r => r.ctSideEnemyFlashTime);
    agg.ctSideUtilityDamage = sumDef(rows, r => r.ctSideUtilityDamage);
    agg.ctSideKillsAgainstPlayers = mergeDicts(rows, r => r.ctSideKillsAgainstPlayers);
    agg.ctSideEKills = sumNull(rows, r => r.ctSideEKills);
    agg.ctSideEDeaths = sumNull(rows, r => r.ctSideEDeaths);
    agg.ctSideEOpenKills = sumNull(rows, r => r.ctSideEOpenKills);
    agg.ctSideEOpenDeaths = sumNull(rows, r => r.ctSideEOpenDeaths);
    agg.ctSideTradedDeaths = sumNull(rows, r => r.ctSideTradedDeaths);
    agg.ctSideETraded = sumNull(rows, r => r.ctSideETraded);
    agg.ctSideADR = weightedAvg(rows, r => r.ctSideADR, r => r.ctSideRoundsPlayed) ?? undefined;
    agg.ctSideKAST = weightedAvg(rows, r => r.ctSideKAST, r => r.ctSideRoundsPlayed) ?? undefined;
    agg.ctSideRating2 = weightedAvg(rows, r => r.ctSideRating2, r => r.ctSideRoundsPlayed) ?? undefined;
    agg.ctSideRating21 = weightedAvg(rows, r => r.ctSideRating21, r => r.ctSideRoundsPlayed) ?? undefined;
    agg.ctSideRating30 = weightedAvg(rows, r => r.ctSideRating30, r => r.ctSideRoundsPlayed);
    agg.ctSideSwingPct = weightedAvg(rows, r => r.ctSideSwingPct, r => r.ctSideRoundsPlayed);
    agg.ctSideEAdr = weightedAvg(rows, r => r.ctSideEAdr, r => r.ctSideRoundsPlayed);
    agg.ctSideEKast = weightedAvg(rows, r => r.ctSideEKast, r => r.ctSideRoundsPlayed);

    // --- Rating 3.0 sub-components: rounds-weighted per component ---
    agg.rating3Subs = aggregateSubs(rows);

    return agg;
};
